import useIsMobile from "@/hooks/useIsMobile";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link, usePage, router } from "@inertiajs/react";
import { m as motion, AnimatePresence } from "motion/react";
import PlatformsMarquee from "@/Components/PlatformsMarquee";
import {
    Search,
    Building2,
    User,
    Star,
    Briefcase,
    ArrowLeft,
    ShieldCheck,
    Users,
    TrendingUp,
    Headset,
    Play,
} from "lucide-react";
import heroCharacterMobile from "../../../../images/home/hero-character-mobile.png?w=360&format=webp";
import sallaLogo from "@images/platforms/Salla.svg";
import tabbyLogo from "@images/payments/tabby.svg";
// 240px WebP of the logo; tamara.svg wraps a 3737px PNG (~600KB gzipped).
import tamaraLogo from "@images/payments/tamara-logo.webp";
import googleLogo from "@images/platforms/Google.svg";
import facebookLogo from "@images/platforms/Facebook.svg";
import snapchatLogo from "@images/platforms/Snapchat.svg";
import tiktokLogo from "@images/platforms/TikTok.svg";

// Popular tags linking to real services
const POPULAR_TAGS = [
    { name: "تصميم جرافيك", slug: "تصميم-جرافيك" },
    { name: "تطوير مواقع", slug: "أفضل-شركة-تصميم-مواقع" },
    { name: "موشن جرافيك", slug: "تصميم-موشن-جرافيك" },
    { name: "تطبيقات جوال", slug: "تصميم-تطبيقات" },
    { name: "فويس اوفر", slug: "فويس-اوفر" },
];

// Services list for search autocomplete
export const SERVICES = [
    { name: "تصميم موقع الكتروني", slug: "أفضل-شركة-تصميم-مواقع" },
    { name: "إدارة المتاجر الإلكترونية", slug: "ادارة-المتاجر-الالكترونية" },
    { name: "تصميم تطبيقات جوال", slug: "تصميم-تطبيقات" },
    { name: "إدارة حسابات التواصل الاجتماعي", slug: "ادارة-حسابات-التواصل-الاجتماعي" },
    { name: "إدارة حملات إعلانية", slug: "ادارة-حملات-اعلانية" },
    { name: "شركة سيو", slug: "شركة-سيو" },
    { name: "تصميم جرافيك", slug: "تصميم-جرافيك" },
    { name: "موشن جرافيك", slug: "تصميم-موشن-جرافيك" },
    { name: "فويس اوفر", slug: "فويس-اوفر" },
    { name: "التسويق عبر المؤثرين", slug: "التسويق-عبر-المؤثرين" },
];

// Keyword pill and lead shared by the mobile and desktop heroes (same
// wording as the homepage meta description).
const HERO_KICKER = "أفضل شركة تسويق إلكتروني في السعودية والخليج";
const HERO_LEAD =
    "من التسويق الإلكتروني وتحسين محركات البحث إلى إدارة الحملات الإعلانية وتطوير المواقع والمتاجر الإلكترونية، نجمع لك الخبرات والأدوات والاستراتيجيات اللازمة لبناء حضور رقمي أقوى وتنمية أعمالك في السعودية والخليج.";

// How-we-work steps under the desktop lead, one set per tab.
const CLIENT_PLAN = [
    { t: "خطة واضحة", d: "نفهم نشاطك وأهدافك ونحدد القنوات والخطوات" },
    { t: "فريق متخصص", d: "تسويق وإعلانات وسيو وتصميم تحت إدارة واحدة" },
    { t: "نتائج تُقاس", d: "تقارير بالأرقام وتحسين مستمر للنمو" },
];
const FREELANCER_PLAN = [
    { t: "أنشئ حسابك", d: "سجّل واعرض مهاراتك وخبراتك" },
    { t: "ابنِ معرض أعمالك", d: "أضف أعمالك وحدد أسعارك بنفسك" },
    { t: "ابدأ العمل", d: "تقدّم على المشاريع المتاحة يوميًا" },
];

const TRUST_BULLETS = ["لا بطاقة ائتمان مطلوبة", "دفع آمن عند الرضا فقط", "ضمان استرداد 7 أيام"];

// Scoped hero styles (same look as the UGC page hero). Plain CSS instead of
// new Tailwind classes, because deploys keep live's pinned app CSS.
const HERO_CSS = `
.wzh-kick-m{display:block;width:fit-content;max-width:100%;margin:12px auto 0;padding:6px 12px;border-radius:999px;background:#eef4ff;color:#1d4ed8;font-size:clamp(11px,3.2vw,13px);font-weight:800;line-height:1.5;text-align:center}
.wzh-kick{display:inline-flex;margin:0 0 14px;padding:6px 18px;border-radius:999px;background:#eef4ff;color:#1d4ed8;font-size:18px;font-weight:800;line-height:1.6}
.wzh-head{max-width:900px;margin:0 auto 24px;text-align:center}
.wzh-h1{margin:0 0 14px;font-size:60px;font-weight:800;line-height:1.3;letter-spacing:-.3px;color:#0B1536}
.wzh-h1 .l1,.wzh-h1 .l2{display:block}
.wzh-h1 em{font-style:normal;color:#2563eb;white-space:nowrap}
.wzh-lead{max-width:760px;margin:0 auto;font-size:18px;font-weight:500;line-height:1.9;color:#4b5563;text-wrap:balance}
.wzh-plan{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;width:100%;max-width:900px;margin:0 0 24px;padding:0;list-style:none}
.wzh-plan li{display:flex;align-items:center;gap:12px;padding:14px 16px;border:1px solid #e8edf5;border-radius:16px;background:#fff;text-align:right}
.wzh-plan .n{display:flex;align-items:center;justify-content:center;flex:0 0 32px;width:32px;height:32px;border-radius:10px;background:#2563eb;color:#fff;font-size:15px;font-weight:800}
.wzh-plan span b{display:block;font-size:16px;font-weight:800;line-height:1.5;color:#0B1536}
.wzh-plan small{display:block;margin-top:2px;font-size:13.5px;line-height:1.7;color:#6b7280}
.wzh-ctas{display:flex;align-items:center;justify-content:center;gap:12px;margin-bottom:18px}
.wzh-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:52px;padding:0 28px;border:1.5px solid transparent;border-radius:999px;font-size:16px;font-weight:800;text-decoration:none;transition:background .15s,border-color .15s,box-shadow .15s}
.wzh-btn svg{width:18px;height:18px}
.wzh-btn.p{background:#2563eb;color:#fff;box-shadow:0 10px 24px -8px rgba(37,99,235,.6)}
.wzh-btn.p:hover{background:#1d4ed8;color:#fff}
.wzh-btn.o{background:#fff;color:#1d4ed8;border-color:#c9d6ff}
.wzh-btn.o:hover{border-color:#2563eb}
.wzh-checks{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:10px 24px;margin:0 0 28px;padding:0;list-style:none}
.wzh-checks li{display:inline-flex;align-items:center;gap:6px;font-size:14px;font-weight:700;color:#334155;white-space:nowrap}
.wzh-checks li>svg{width:16px;height:16px;flex:none;color:#16a34a}
.wzh-checks .rate b{font-size:16px;font-weight:800;color:#0B1536}
.wzh-checks .stars{display:inline-flex;gap:2px;color:#facc15}
.wzh-checks .stars svg{width:15px;height:15px;fill:currentColor}
`;

const TabButton = ({ active, onClick, id, icon, label, shortLabel }) => (
    <button
        role="tab"
        type="button"
        aria-selected={active}
        aria-controls={id}
        onClick={onClick}
        className={`relative flex items-center gap-1.5 md:gap-2 px-3 md:px-6 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-bold transition-colors z-10 ${
            active ? "text-white" : "text-gray-500 hover:text-gray-900"
        }`}
    >
        {icon}
        {shortLabel ? (
            <>
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">{shortLabel}</span>
            </>
        ) : (
            label
        )}
        {active && (
            <motion.div
                layoutId="activeTabBadge"
                className="absolute inset-0 bg-primary-600 rounded-full z-[-1] shadow-md shadow-primary-500/30"
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
            />
        )}
    </button>
);

const HeroV2 = () => {
    // One visible <h1> per breakpoint: mobile renders the mobile heading as
    // the H1, desktop renders its own heading as the H1 (never both in DOM).
    const isMobile = useIsMobile();
    const [activeTab, setActiveTab] = useState("client"); // 'client' or 'freelancer'
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const searchRef = useRef(null);
    const inputRef = useRef(null);
    const { clientLogos = [] } = usePage().props;

    // Close autocomplete on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsSearchFocused(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setIsSearchFocused(false);
                if (inputRef.current) inputRef.current.blur();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, []);

    const filteredServices = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return SERVICES;
        return SERVICES.filter((service) =>
            service.name.toLowerCase().includes(q)
        );
    }, [searchQuery]);

    const handleSearchSubmit = useCallback(
        (e) => {
            e.preventDefault();
            const q = searchQuery.trim();
            if (!q) {
                setIsSearchFocused(true);
                inputRef.current?.focus();
                return;
            }
            // Best match (first filtered service) or generic search
            const best = filteredServices[0];
            const target = best
                ? `/services/${best.slug}`
                : `/search?q=${encodeURIComponent(q)}`;
            setIsSearchFocused(false);
            router.visit(target);
        },
        [searchQuery, filteredServices]
    );

    const heroPanelId =
        activeTab === "client" ? "hero-panel-client" : "hero-panel-freelancer";

    return (
        <div className="relative pb-16 lg:pb-24 overflow-hidden">
            <style>{HERO_CSS}</style>
            <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-primary-400/10 rounded-full blur-[60px] md:blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-10 w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-[50px] md:blur-[100px] pointer-events-none" />

            {/* Mobile — completely different composition from desktop, not a
                resized version of it: no search bar, no client/freelancer
                tab switcher (both real functional UI, dropped here per
                explicit confirmation — desktop keeps them, untouched,
                below). Short H1 → description → 2 CTAs → 3 compact
                benefits → character photo → trust strip. */}
            <div className="hero-mobile-fill lg:hidden wrapper relative z-10 flex flex-col">
                <p className="wzh-kick-m">{HERO_KICKER}</p>
                {/* Mobile hero blocks use initial={false}: phones get prerendered markup,
                    and a fade-in from opacity 0 would hold the LCP until the JS runs. */}
                <motion.div
                    initial={false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                    className="mt-3 text-center text-[34px] font-extrabold leading-[1.25] text-[#0B1536] text-balance"
                >
                    {isMobile ? (
                    <h1 className="m-0 text-inherit font-inherit leading-inherit" style={{ font: "inherit" }}>
                    كبّر مشروعك مع
                    <br />
                    <span className="text-primary-600">أفضل الخبراء</span> في السعودية
                    </h1>
                    ) : (
                    <span>
                    كبّر مشروعك مع
                    <br />
                    <span className="text-primary-600">أفضل الخبراء</span> في السعودية
                    </span>
                    )}
                </motion.div>

                <motion.p
                    initial={false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mt-3 text-center text-[15px] leading-relaxed text-gray-500 font-medium max-w-[320px] mx-auto"
                    style={{ maxWidth: 352, fontSize: 14.5, lineHeight: 1.8 }}
                >
                    {HERO_LEAD}
                </motion.p>

                <motion.div
                    initial={false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="mt-7 flex flex-col items-center gap-3"
                >
                    <Link
                        href="/client/register"
                        className="inline-flex items-center gap-3 rounded-full bg-primary-600 text-white font-bold py-2 pr-6 pl-2 shadow-lg shadow-primary-600/25 text-[16px]"
                    >
                        ابدأ الآن
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-primary-600">
                            <ArrowLeft className="h-4 w-4" />
                        </span>
                    </Link>
                    <Link
                        href="/portfolio"
                        className="inline-flex items-center gap-3 rounded-full bg-white border border-gray-200 text-gray-800 font-bold py-2 pr-6 pl-2 text-[14px]"
                    >
                        شاهد كيف نعمل
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-primary-600">
                            <Play className="h-3.5 w-3.5 fill-current" />
                        </span>
                    </Link>
                </motion.div>

                {/* Strict 3-equal-column grid (not flex-wrap) — guarantees
                    all 3 stay on one row at 360–430px instead of the
                    longest label ("نتائج قابلة للقياس") wrapping to a
                    second line. Icon stacked above label (not beside it) so
                    each ~1/3-width column has room for the longest label
                    without overflowing. */}
                <motion.div
                    initial={false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mt-5 grid grid-cols-3 divide-x divide-gray-200 max-w-[340px] mx-auto"
                >
                    {[
                        { Icon: Users, label: "خبراء متخصصون" },
                        { Icon: TrendingUp, label: "نتائج قابلة للقياس" },
                        { Icon: Headset, label: "دعم مستمر" },
                    ].map(({ Icon, label }) => (
                        <div key={label} className="flex flex-col items-center justify-center gap-1 px-1.5 text-center">
                            <Icon className="h-5 w-5 text-primary-600" strokeWidth={1.75} />
                            <span className="text-[11px] font-bold leading-tight text-gray-700">{label}</span>
                        </div>
                    ))}
                </motion.div>

                {/* Character over a light abstract-blob backdrop with a
                    faint skyline strip, a floating growth-stat card, and a
                    handwritten-style note — restored per the reference
                    screenshot (this exact treatment was built once, then
                    removed for being "crowded", then requested again — this
                    is the version to keep going forward). */}
                <motion.div
                    initial={false}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.25, duration: 0.6 }}
                    className="hero-mobile-photo relative mt-4 -mx-4 flex flex-1 flex-col min-h-0"
                    style={{ minHeight: 220 }}
                >
                    <div className="relative flex-1 min-h-0 overflow-hidden" style={{ maxHeight: 340, minHeight: 220 }}>
                        <div
                            className="absolute inset-0"
                            style={{ background: "linear-gradient(180deg, #EAF2FF 0%, #F5F9FF 60%, #FFFFFF 100%)" }}
                            aria-hidden="true"
                        />
                        <div
                            className="absolute left-1/2 top-2 h-64 w-64 -translate-x-1/2 rounded-[40%] bg-primary-100/60 blur-2xl"
                            aria-hidden="true"
                        />
                        {/* Faint skyline silhouette */}
                        <svg
                            viewBox="0 0 400 90"
                            preserveAspectRatio="none"
                            className="absolute inset-x-0 bottom-0 h-16 w-full text-primary-200/70"
                            aria-hidden="true"
                        >
                            <rect x="10" y="30" width="24" height="60" fill="currentColor" />
                            <rect x="40" y="15" width="18" height="75" fill="currentColor" />
                            <polygon points="185,90 200,90 204,10 196,10" fill="currentColor" />
                            <polygon points="215,90 200,90 196,10 204,10" fill="currentColor" />
                            <rect x="340" y="35" width="22" height="55" fill="currentColor" />
                            <rect x="368" y="20" width="16" height="70" fill="currentColor" />
                        </svg>

                        {/* Decorative growth-data motion, fully behind the
                            character (lower z-index than the photo, never
                            overlapping his face/body since it's confined to
                            this container). Pure CSS transform/opacity
                            animation (see <style> below) — no Framer Motion,
                            no JS, no extra render cost, safe for LCP/CLS.
                            prefers-reduced-motion disables it entirely. */}
                        <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true">
                            <svg viewBox="0 0 300 200" className="hero-chart-breathe absolute bottom-6 right-2 h-28 w-40 text-primary-300">
                                <defs>
                                    <linearGradient id="heroChartFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
                                        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                <polygon
                                    points="0,150 50,120 100,130 150,80 200,90 250,40 300,55 300,200 0,200"
                                    fill="url(#heroChartFill)"
                                />
                                <polyline
                                    points="0,150 50,120 100,130 150,80 200,90 250,40 300,55"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    opacity="0.8"
                                />
                                {[[0, 150], [100, 130], [200, 90], [300, 55]].map(([x, y]) => (
                                    <circle key={x} cx={x} cy={y} r="4" fill="white" stroke="currentColor" strokeWidth="2.5" />
                                ))}
                            </svg>
                            <span className="hero-num-float absolute right-6 top-14 text-[11px] font-black text-primary-400/60" style={{ animationDelay: "0s" }}>+24%</span>
                            <span className="hero-num-float absolute right-20 top-24 text-[11px] font-black text-primary-400/50" style={{ animationDelay: "1.2s" }}>+38%</span>
                            <span className="hero-num-float absolute left-6 top-10 text-[11px] font-black text-primary-400/50" style={{ animationDelay: "2.1s" }}>+52%</span>
                            <span className="hero-num-pulse absolute right-10 bottom-16 text-[11px] font-black text-primary-400/40">+71%</span>
                        </div>

                        <img
                            src={heroCharacterMobile}
                            alt="خبير تسويق رقمي سعودي في ويز فريلانس"
                            className="absolute bottom-0 left-1/2 w-auto -translate-x-1/2 object-contain"
                            style={{ height: "94%" }}
                            loading="eager"
                            fetchPriority="high"
                            decoding="async"
                        />

                        {/* Handwritten-style note */}
                        <p
                            className="absolute left-6 top-6 max-w-[110px] -rotate-6 text-[13px] font-bold italic leading-tight text-primary-700/80"
                            aria-hidden="true"
                        >
                            معاً نصنع قصص نجاح أكبر
                        </p>
                    </div>
                </motion.div>

                {/* Pure CSS, GPU-friendly (transform + opacity only),
                    continuous and slow — no JS animation loop, no layout
                    properties touched, so no CLS risk. Disabled entirely
                    under prefers-reduced-motion. */}
                <style>{`
                    .hero-chart-breathe {
                        animation: heroChartFloat 4s ease-in-out infinite alternate;
                        will-change: transform;
                    }
                    @keyframes heroChartFloat {
                        from { transform: translate3d(0, 3px, 0); }
                        to { transform: translate3d(0, -3px, 0); }
                    }
                    .hero-num-float {
                        animation: heroNumFloat 4.5s ease-in-out infinite alternate;
                        will-change: transform;
                    }
                    @keyframes heroNumFloat {
                        from { transform: translate3d(0, 2px, 0); }
                        to { transform: translate3d(0, -2px, 0); }
                    }
                    .hero-num-pulse {
                        animation: heroNumPulse 3s ease-in-out infinite;
                        will-change: opacity, transform;
                    }
                    @keyframes heroNumPulse {
                        0%, 100% { opacity: 0.35; transform: scale(1); }
                        50% { opacity: 0.75; transform: scale(1.06); }
                    }
                    /* Continuous seamless loop (linear, no alternate):
                       slides exactly one logo copy (+50%) to the right —
                       under dir="rtl" sliding left left an empty gap. */
                    .hero-logo-marquee {
                        animation: heroLogoMarquee 22s linear infinite;
                        will-change: transform;
                    }
                    @keyframes heroLogoMarquee {
                        from { transform: translate3d(0, 0, 0); }
                        to { transform: translate3d(50%, 0, 0); }
                    }
                    /* First-screen fit: content starts 160px down (120px
                       on md), minus a 12px breathing gap at the bottom. */
                    .hero-mobile-fill {
                        min-height: calc(100svh - 172px);
                    }
                    @media (min-width: 768px) {
                        .hero-mobile-fill { min-height: calc(100svh - 132px); }
                    }
                    /* Short phones (SE / small Android): text + CTAs already
                       fill the screen, so the photo would shrink to a sliver —
                       drop it and anchor the logo card to the bottom instead. */
                    @media (max-width: 767px) and (max-height: 700px) {
                        .hero-mobile-photo { display: none; }
                        .hero-trust-card { margin-top: auto; }
                        .hero-mobile-fill > h1 { font-size: 27px; margin-top: 4px; }
                        .hero-mobile-fill > .mt-7 { margin-top: 16px; gap: 10px; }
                        .hero-mobile-fill > .mt-5 { margin-top: 14px; margin-bottom: 12px; }
                    }
                    @media (prefers-reduced-motion: reduce) {
                        .hero-chart-breathe, .hero-num-float, .hero-num-pulse, .hero-logo-marquee {
                            animation: none;
                        }
                    }
                `}</style>

                {/* Pinned to the bottom edge of the first screen on every phone
                    (2026-10-07): the wrapper fills the viewport and the photo
                    above flexes, so this card always ends at the screen bottom.
                    Trust card — real dynamic client logos + real platform
                    logos (Salla only — Zid/shipping-company logo files
                    don't exist anywhere in this project, so they're left
                    out rather than fabricated; add the real files and I'll
                    wire them in). Auto-scrolling marquee, no manual-scroll
                    affordance — duplicated logo set for a seamless loop,
                    same technique as the header's promo ticker. */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="hero-trust-card relative -mt-6 rounded-[20px] bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.08)]"
                >
                    <p className="text-center text-[13px] font-bold text-gray-700">
                        نعمل بتكامل كامل مع أبرز المنصات الرقمية
                    </p>
                    <div className="mt-3 overflow-hidden">
                        <div className="hero-logo-marquee flex w-max items-center">
                            {[0, 1].map((rep) => (
                                <div key={rep} className="flex shrink-0 items-center gap-6" style={{ paddingInlineEnd: 24 }} aria-hidden={rep === 1 ? "true" : undefined}>
                                    <div className="shrink-0 h-7 w-14 flex items-center justify-center">
                                        <img src={sallaLogo} alt="سلة" loading="eager" className="h-full w-full object-contain" />
                                    </div>
                                    <div className="shrink-0 h-6 w-14 flex items-center justify-center">
                                        <img src={tabbyLogo} alt="تابي" loading="eager" className="h-full w-full object-contain" />
                                    </div>
                                    <div className="shrink-0 h-5 w-14 flex items-center justify-center">
                                        <img src={tamaraLogo} alt="تمارا" loading="eager" className="h-full w-full object-contain" />
                                    </div>
                                    <div className="shrink-0 h-6 w-14 flex items-center justify-center">
                                        <img src={googleLogo} alt="Google" loading="eager" className="h-full w-full object-contain" />
                                    </div>
                                    <div className="shrink-0 h-6 w-14 flex items-center justify-center">
                                        <img src={facebookLogo} alt="Meta" loading="eager" className="h-full w-full object-contain" />
                                    </div>
                                    <div className="shrink-0 h-6 w-14 flex items-center justify-center">
                                        <img src={snapchatLogo} alt="Snapchat" loading="eager" className="h-full w-full object-contain" />
                                    </div>
                                    <div className="shrink-0 h-6 w-14 flex items-center justify-center">
                                        <img src={tiktokLogo} alt="TikTok" loading="eager" className="h-full w-full object-contain" />
                                    </div>
                                    {/* Real client logos only — populated the moment
                                        anything is uploaded at /dashboard/client-logos.
                                        No placeholder boxes when empty. */}
                                    {clientLogos.slice(0, 8).map((client, i) => (
                                        <div key={client?.id ?? i} className="shrink-0 h-7 w-16 grayscale opacity-60 flex items-center justify-center">
                                            <img
                                                src={client.image_thumb || client.image_path}
                                                alt={client.alt_text || "عميل"}
                                                loading="eager"
                                                className="h-full w-full object-contain"
                                            />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Desktop/tablet — unchanged from before this mobile pass:
                tabs, search bar, popular tags, full trust section, platform
                marquee. */}
            <div className="hidden lg:block">

            <div className="wrapper relative z-10 max-w-5xl mx-auto flex flex-col items-center">
                {/* Top Tabs Switcher */}
                <div
                    role="tablist"
                    aria-label="نوع الحساب"
                    className="flex bg-gray-100 p-1 md:p-1.5 rounded-full mb-6 md:mb-8 shadow-inner border border-gray-200/60 transition-all duration-300 hover:shadow-md"
                >
                    <TabButton
                        id="hero-panel-client"
                        active={activeTab === "client"}
                        onClick={() => setActiveTab("client")}
                        icon={<Building2 className="w-3.5 h-3.5 md:w-4 md:h-4" />}
                        label="صاحب مشروع"
                    />
                    <TabButton
                        id="hero-panel-freelancer"
                        active={activeTab === "freelancer"}
                        onClick={() => setActiveTab("freelancer")}
                        icon={<User className="w-3.5 h-3.5 md:w-4 md:h-4" />}
                        label="مستقل"
                    />
                </div>

                {/* Everything below reacts to the selected tab */}
                <div id={heroPanelId} role="tabpanel" className="contents">

                {/* UGC-style layout (2026-10-20, the UGC page hero was the
                    user's reference): keyword pill → H1 → lead → three
                    numbered steps → CTAs → trust checks, then the search,
                    popular tags and partner badges. Scoped .wzh-* rules in
                    HERO_CSS because the live Tailwind build is pinned. */}
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="wzh-kick"
                >
                    {activeTab === "client" ? HERO_KICKER : "شبكة مستقلي ويز فريلانس"}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
                    className="wzh-head"
                >
                    {/* The mobile block above keeps the page's real <h1>
                        (mobile-first indexing); on mobile this renders as a
                        div so the DOM never holds two H1s. */}
                    {(() => { const DeskHeading = isMobile ? "div" : "h1"; return (
                    <DeskHeading className="wzh-h1">
                        {activeTab === "client" ? (
                            <>
                                <span className="l1">شريك <em>النمو الرقمي</em></span>{" "}
                                <span className="l2">الذي يبني فريق نجاح مشروعك</span>
                            </>
                        ) : (
                            <>
                                <span className="l1">ابدأ مسيرتك وانضم <em>لأفضل</em></span>{" "}
                                <span className="l2">شبكة مستقلين بالمنطقة</span>
                            </>
                        )}
                    </DeskHeading>
                    ); })()}
                    <p className="wzh-lead">
                        {activeTab === "client"
                            ? HERO_LEAD
                            : "آلاف المشاريع بانتظارك يومياً، ابنِ معرض أعمالك، وحدد أسعارك، وانطلق نحو الاستقلالية المادية بسهولة."}
                    </p>
                </motion.div>

                <motion.ol
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="wzh-plan"
                >
                    {(activeTab === "client" ? CLIENT_PLAN : FREELANCER_PLAN).map((step, i) => (
                        <li key={step.t}>
                            <b className="n">{i + 1}</b>
                            <span>
                                <b>{step.t}</b>
                                <small>{step.d}</small>
                            </span>
                        </li>
                    ))}
                </motion.ol>

                {/* RTL row: the first child (primary CTA) sits on the right. */}
                <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="wzh-ctas"
                >
                    {activeTab === "client" ? (
                        <>
                            <Link href="/client/register" className="wzh-btn p">
                                أضف مشروعك
                                <ArrowLeft aria-hidden="true" />
                            </Link>
                            <Link href="/portfolio" className="wzh-btn o">
                                تصفح الأعمال
                            </Link>
                        </>
                    ) : (
                        <Link href="/freelancer/register" className="wzh-btn p">
                            تسجيل كـ مستقل
                            <ArrowLeft aria-hidden="true" />
                        </Link>
                    )}
                </motion.div>

                <motion.ul
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="wzh-checks"
                >
                    <li className="rate">
                        <b>5.0</b>
                        <span className="stars" aria-label="تقييم 5 من 5">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star key={i} aria-hidden="true" />
                            ))}
                        </span>
                        +1265 مشروعًا منفّذًا
                    </li>
                    {TRUST_BULLETS.map((bullet) => (
                        <li key={bullet}>
                            <ShieldCheck aria-hidden="true" />
                            {bullet}
                        </li>
                    ))}
                </motion.ul>

                {/* Search Bar */}
                <motion.form
                    onSubmit={handleSearchSubmit}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="w-full max-w-2xl mx-auto relative mb-6"
                    ref={searchRef}
                    role="search"
                >
                    <label htmlFor="hero-search" className="sr-only">
                        بحث عن الخدمات
                    </label>
                    <div
                        className={`relative flex items-center bg-white border-2 transition-all duration-300 rounded-full shadow-sm overflow-visible ${
                            isSearchFocused
                                ? "border-primary-500 shadow-primary-200/50 shadow-lg ring-4 ring-primary-50"
                                : "border-gray-200 hover:border-gray-300"
                        }`}
                    >
                        <div className="hidden sm:block pr-5 text-gray-400">
                            <Search className="w-5 h-5" />
                        </div>
                        <input
                            id="hero-search"
                            ref={inputRef}
                            type="text"
                            placeholder="ماذا تحتاج؟"
                            className="flex-1 py-3 sm:py-4 px-3 sm:px-4 bg-transparent border-none outline-none focus:ring-0 text-sm sm:text-base text-gray-700 font-medium placeholder-gray-400"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => setIsSearchFocused(true)}
                            autoComplete="off"
                            role="combobox"
                            aria-autocomplete="list"
                            aria-expanded={isSearchFocused && filteredServices.length > 0}
                            aria-controls="hero-search-listbox"
                        />
                        <button
                            type="submit"
                            aria-label="ابحث"
                            className="bg-primary-600 hover:bg-primary-700 text-white font-bold p-2.5 sm:py-3 sm:px-4 md:px-8 rounded-full ml-1 md:ml-1.5 transition-colors shadow-md shadow-primary-500/20 text-sm md:text-base flex items-center justify-center"
                        >
                            <Search className="w-5 h-5 sm:hidden" />
                            <span className="hidden sm:inline">ابحث</span>
                        </button>
                    </div>

                    {/* Autocomplete Dropdown */}
                    <AnimatePresence>
                        {isSearchFocused && filteredServices.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.98 }}
                                className="absolute left-0 right-0 top-full mt-3 bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden z-20"
                            >
                                <ul
                                    id="hero-search-listbox"
                                    role="listbox"
                                    className="max-h-64 overflow-y-auto p-2"
                                >
                                    {filteredServices.map((service, idx) => (
                                        <li key={`${service.slug}-${idx}`} role="option">
                                            <Link
                                                href={`/services/${service.slug}`}
                                                onClick={() => setIsSearchFocused(false)}
                                                className="flex items-center gap-3 w-full p-3 hover:bg-blue-50 rounded-xl transition-colors text-right"
                                            >
                                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-primary-600">
                                                    <Briefcase className="w-4 h-4" />
                                                </div>
                                                <span className="font-bold text-gray-700">
                                                    {service.name}
                                                </span>
                                                <ArrowLeft className="w-4 h-4 mr-auto text-gray-400" />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.form>

                {/* Popular Tags */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="flex flex-wrap items-center justify-center gap-2 mb-8 md:mb-10 text-xs md:text-sm px-2"
                >
                    <span className="text-gray-600 font-semibold ml-2 w-full md:w-auto text-center md:text-left">
                        الأكثر طلباً:
                    </span>
                    {POPULAR_TAGS.map((tag) => (
                        <Link
                            key={tag.slug}
                            href={`/services/${tag.slug}`}
                            className="px-2.5 py-1 md:px-3 md:py-1 bg-white border border-gray-200 text-gray-600 font-medium rounded-full hover:border-primary-300 hover:text-primary-600 hover:bg-blue-50 transition-colors text-xs md:text-sm"
                        >
                            {tag.name}
                        </Link>
                    ))}
                </motion.div>

                {/* Certified partner badges. */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.8 }}
                    className="w-full max-w-4xl border-t border-gray-200/60 pt-8"
                >
                    <PlatformsMarquee />
                </motion.div>
                </div>
            </div>
            </div>
        </div>
    );
};

export default HeroV2;
